<template>
  <div>
    <div class="grid-x hide-for-print">
      <div class="small-order-1 cell margin-bottom-m">
        <MedalliaSurvey page="return-confirmation" />
        <h1 tabindex="0" v-text="pageTitle"></h1>
      </div>
      <div class="small-order-2 large-auto small-12">
        <div :class="{ 'margin-bottom-m': ssreSubmission }">
          <p
            v-if="showSubHeader"
            id="sub-header"
            aria-hidden="false"
            :aria-label="returnConfirmation.subHeader[`${singleRMA.returnResolution}_ARIA_LABEL`]"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span aria-hidden="true" v-html="subHeaderText"></span>
          </p>
          <p id="email-confirmation" class="margin-top-xxs" tabindex="0">
            <span v-text="emailIntimationText"></span>
            <span class="heavy" v-text="response.returnEmail"></span>
          </p>
          <Notification
            v-if="returnConfirmationStore.returnLabel.showNotification && !!singleRMA"
            class="margin-top-xs"
            severity="success"
            :closable="false"
            aria-live="assertive"
          >
            We emailed your return confirmation to
            {{ returnConfirmationStore.returnLabel.emailAddress }}.
          </Notification>
          <button v-if="!!singleRMA" class="margin-top-xs link-body" type="button" @click="resendEmailHandler">Resend email</button>
          <Notification
            v-for="notification in notifications"
            :key="notification.text"
            :class="notification.class || 'margin-top-s'"
            :severity="notification.type"
            :closable="notification.closable || false"
            aria-live="polite"
            aria-atomic="true"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="notification.text"></div>
          </Notification>
          <div v-if="ssreSubmission" class="margin-top-m">
            <!-- Row 1 -->
            <div class="grid-x small-12 grid-margin-x">
              <!-- QRCode (only for label-free) -->
              <div v-if="singleRMA.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree" class="small-12 large-3 cell">
                <QRCode :qrcode-url="singleRMA.dropOffUrl" :scan-text="getScanText" priority="high" />
              </div>

              <!-- Return Instructions -->
              <div
                :class="[
                  'cell small-12',
                  singleRMA.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree ? 'small-margin-top-m large-margin-top-z large-6' : 'large-8',
                ]"
              >
                <ReturnInstructions
                  :return-drop-off-method="singleRMA.returnDropOffMethod"
                  :refund-method="singleRMA.refundMethod"
                  :return-resolution="singleRMA.returnResolution"
                  :items="singleRMA.rmaLines"
                  :is-mkp-order="singleRMA.isMkpOrder"
                  make-ups-clickable
                  :is-replace-exchange="ssreSubmission"
                  :is-boris-return="isBorisReturn"
                  :drop-off-date="singleRMA.dropOffByDate"
                  @ups-click="showLocatorSlideout"
                />
              </div>

              <!-- Delivery Information -->
              <div :class="['cell small-12', singleRMA.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree ? 'large-3' : 'large-4']">
                <DeliveryInformation
                  class="small-border-top small-margin-top-m small-padding-top-m large-padding-top-z large-margin-top-z"
                  page="return-confirmation"
                  :data="singleRMA.deliveryContact"
                />
              </div>
            </div>

            <!-- Row 2: Print Button -->
            <div v-if="showPrintLabel" class="cell small-12 margin-top-s">
              <AppButton id="print-label" ref="printLabelRef" class="is-visible primary button expanded cta" @click.prevent="printLabel">
                Print Return Label
              </AppButton>
            </div>
          </div>
        </div>
        <div v-if="ssreSubmission" class="border-top" :class="{ 'margin-bottom-m': ssreSubmission }">
          <h5 class="margin-top-m" v-text="trackerTitle"></h5>
          <ProgressBar class="margin-top-m" :progress-bar-data="progressBarData" progress-bar-value="1" />
        </div>
        <RMADetail
          v-for="(rma, index) in response.rmaList"
          :key="rma.rmaId"
          :is-single-rma="!!singleRMA"
          :rma-data="rma"
          :packages-count="response.packagesCount"
          :is-boris-return="isBorisReturn"
          :is-first-package="index === 0"
        />
      </div>
      <!-- Refund summary details -->
      <div
        v-if="summaryData"
        role="region"
        aria-label="Summary Section"
        class="small-order-4 small-margin-top-s large-order-3 small-12 large-offset-1 large-margin-top-z"
        :class="singleRMA.returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree ? 'large-3' : 'large-4'"
      >
        <SummarySection :summary="summaryData" :ssre-submission="ssreSubmission" :return-resolution="singleRMA.returnResolution" />
      </div>
      <div v-if="showRefundInstructions" class="small-order-4 large-order-3 border-top large-7 small-12">
        <RefundInstructions :return-details="singleRMA" aria-live="off" show-title :is-boris-return="isBorisReturn" />
      </div>
      <!-- eslint-disable max-len -->
      <div
        v-if="ssreSubmission"
        class="border-top border-bottom padding-top-m padding-bottom-m small-order-3 small-12 small-margin-top-m large-order-4 large-7 large-margin-top-z"
      >
        <h5 v-text="returnConfirmation.ssreReturnDropOffWindow.header"></h5>
        <p class="margin-top-xxs" :aria-label="returnConfirmation.ssreReturnDropOffWindow.aria_label_instruction(singleRMA.returnResolution)">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span aria-hidden="true" v-html="ssreDropOffWindowInstruction"></span>
        </p>
      </div>
    </div>
    <ProsWrapper
      class="small-margin-top-s small-padding-top-s large-padding-top-z small-border-top large-margin-top-m hide-for-print"
      align-horizontal
      data-context="PR_ZONE_A"
      :items="prosItems"
    />
    <div v-if="showPrintPreview" class="print-return-submitted-label-container">
      <PrintReturnLabel :return-details="enhancedprintLabelData" :ssre-type="singleRMA.returnResolution" />
    </div>
  </div>
</template>

<script setup>
// Imports
import { computed, defineAsyncComponent, markRaw, onMounted, ref, nextTick } from 'vue';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';

// Local Components
import ProsWrapper from '@/components/ProsWrapper.vue';
import RMADetail from '@/components/returns/RMADetail.vue';
import DeliveryInformation from '@/components/common/DeliveryInformation.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import ReturnInstructions from '@/components/returns/ReturnInstructions.vue';
import MedalliaSurvey from '@/components/MedalliaSurvey.vue';
import SummarySection from '@/components/returns/SummarySection.vue';
import PrintReturnLabel from '@/components/returns/PrintReturnLabel.vue';
import AppButton from '@/components/AppButton.vue';
import QRCode from '@/components/common/QRCode.vue';

// Stores & Composables
import { useReturnConfirmationStore } from '@/stores/returns/useReturnConfirmationStore';
import { useSlideout } from '@/composables/useSlideout';
import { useModal } from '@/composables/useModal';
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';

// Config & Constants
import {
  RMA_DETAIL,
  RMA_SHIPPING_DETAILS_ERROR,
  RETURN_METHOD_KEY_MAP,
  returnConfirmation,
  RETURN_RESOLUTIONS,
  LOCATOR_TITLE,
  SCAN_QR_TEXT,
} from 'config';
import { LOCATOR_LOCATION_RADIUS, LOCATOR_RESULT_LIMIT } from '#shared/constants';

const { registerModal, toggleModal } = useModal();

const returnConfirmationStore = useReturnConfirmationStore();
const returnItemsStore = useReturnItemStore();
const envPropsStore = useEnvPropsStore();

const { registerSlideout, toggleSlideout } = useSlideout();

const showPrintPreview = ref(false);
const printLabelRef = ref(null);

const progressBarData = computed(() => {
  const data = [
    { shipmentLabel: 'Submitted', statusBarValue: 1 },
    { shipmentLabel: 'Dropped off', statusBarValue: 33 },
  ];
  if (!envPropsStore.isMew) {
    data.push({ shipmentLabel: 'Shipped', statusBarValue: 66 });
    data.push({ shipmentLabel: 'Delivered', statusBarValue: 100 });
  }
  return data;
});

const returnResponse = computed(() => returnConfirmationStore.returnDetails?.returnResponse ?? []);
const isError = computed(() => returnItemsStore.errors?.getShippingDetails);
const shippingDetails = computed(() => returnItemsStore.shippingDetails);

const getShippingDetails = (payload) => returnItemsStore.GET_SHIPPING_DETAILS(payload);
const getShippingDetailsByRmaId = (payload) => returnItemsStore.GET_SHIPPING_DETAILS_BY_RMA_ID(payload);

const response = computed(() => {
  const order = returnResponse.value[0];
  if (!order) return { rmaList: [], packagesCount: {}, rmaTypes: [] };

  const packagesCount = {};
  const rmaTypes = [];

  return {
    ...order,
    rmaList: order.rmaList.map((rma) => {
      if (!rmaTypes.includes(rma.rmaType)) rmaTypes.push(rma.rmaType);
      const packageKey = `${rma.rmaType}_${rma.returnDropOffMethod}`;
      packagesCount[packageKey] = (packagesCount[packageKey] || 0) + 1;
      const packageSequence = packagesCount[packageKey];

      return {
        ...rma,
        orderNumber: order.orderNumber,
        reservationNumber: order.reservationNumber,
        returnEmail: order.returnEmail,
        fineJewelryProcessingDuration: order.fineJewelryProcessingDuration,
        inTransitDuration: order.inTransitDuration,
        giftCardDuration: order.giftCardDuration,
        refundWindow: order.refundWindow,
        conditionalReturnFeeEnabled: order.conditionalReturnFeeEnabled,
        eWasteFeeEnabled: order.eWasteFeeEnabled,
        isMkpOrder: order.isMkpOrder,
        packageSequence,
        packageKey,
        isNewGoogleAPICallEnabled: order.isNewGoogleAPICallEnabled,
      };
    }),
    packagesCount,
    rmaTypes,
  };
});

const singleRMA = computed(() => {
  if (response.value.rmaList.length === 1) return response.value.rmaList[0];
  return false;
});
const prosItems = computed(() =>
  response.value.rmaList.flatMap((rma) =>
    rma.rmaLines.map((line) => ({
      productId: line.productId,
    })),
  ),
);

function showLocatorSlideout(event) {
  const rmaContactAddress = singleRMA.value.contact?.address || {};
  const address = [
    rmaContactAddress.addressLine1,
    rmaContactAddress.addressLine2,
    rmaContactAddress.addressLine3,
    rmaContactAddress.city,
    rmaContactAddress.state,
    rmaContactAddress.postalCode,
  ].reduce((acc, field) => {
    if (!field) return acc;
    if (acc) return `${acc}, ${field}`;
    return field;
  }, '');

  registerSlideout({
    id: 'return-submitted-locator-slideout',
    title: LOCATOR_TITLE[singleRMA.value.returnDropOffMethod],
    elementIdToFocus: event?.target.id,
    props: {
      typeOf: singleRMA.value.returnDropOffMethod,
      address,
      newGoogleApiCall: singleRMA.value.isNewGoogleAPICallEnabled,
      useMspLocatorApi: true,
      locationRadius: LOCATOR_LOCATION_RADIUS,
      resultsDisplayed: LOCATOR_RESULT_LIMIT,
      showMyStoreCTA: false,
      separateOutlets: true,
      page: 'returns',
      excludeGob: true,
      storeInPreferenceDatabaseEnabled: singleRMA.value?.storeInPreferenceDatabaseEnabled,
      newPlacesDetailsApiEnabled: singleRMA.value?.newPlacesDetailsApiEnabled,
      commonLocationServiceApiKey: envPropsStore.envProps.xMacysApiKey,
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "locator-slideout" */ '@nuxt-common/locator/app/components/Locator'))),
  });

  toggleSlideout('return-submitted-locator-slideout');
}

const ssreSubmission = computed(() => [RETURN_RESOLUTIONS.REPLACEMENT, RETURN_RESOLUTIONS.EXCHANGE].includes(singleRMA.value?.returnResolution));

const showSubHeader = computed(() => {
  if (ssreSubmission.value) return true;
  if (!singleRMA.value) return true;
  return false;
});
const trackerTitle = computed(() =>
  `Track ${singleRMA.value.returnResolution}`.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()),
);
const subHeaderText = computed(() => {
  // TODO: Handle Multiple RMA with mixed RMA type copy
  if (ssreSubmission.value) return returnConfirmation.subHeader[singleRMA.value.returnResolution];
  const shippedReturnPackageCount = response.value.packagesCount[`${RMA_DETAIL.RETURN_REQUIRED.TYPE}_${RETURN_METHOD_KEY_MAP.ups}`];
  if (shippedReturnPackageCount > 1 && !response.value.rmaTypes.includes(RMA_DETAIL.REFUND_RETURN_NOT_REQUIRED.TYPE))
    return `Your return should be sent back in ${shippedReturnPackageCount} separate packages`;
  return 'Your return includes items to ship back and items you can keep.';
});

const isBorisReturn = computed(() => {
  const isStoreReturn = response.value.rmaList[0].returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn;
  return isStoreReturn && response.value.isMkpOrder;
});

const emailIntimationText = computed(() => {
  const isUPSReturn = response.value.rmaList[0].returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups;
  const isFedexReturn = response.value.rmaList[0].returnDropOffMethod === RETURN_METHOD_KEY_MAP.fedex;
  const isLabelFreeReturn = response.value.rmaList[0].returnDropOffMethod === RETURN_METHOD_KEY_MAP.labelFree;
  let submissionType = 'return';
  if (ssreSubmission.value) submissionType = singleRMA.value.returnResolution.toLowerCase();
  if (isBorisReturn.value) {
    return 'We emailed your return confirmation with your return details to ';
  }

  if (
    isUPSReturn ||
    isLabelFreeReturn ||
    isFedexReturn ||
    (singleRMA.value && singleRMA.value.rmaType === RMA_DETAIL.REFUND_RETURN_NOT_REQUIRED.TYPE)
  ) {
    return `We emailed your ${submissionType} confirmation to `;
  }
  return 'We emailed your return confirmation and QR code to ';
});

const isMarketplaceOrder = computed(() => response.value.isMkpOrder);

const pageTitle = computed(() => returnConfirmation.pageTitle[singleRMA.value.returnResolution || RETURN_RESOLUTIONS.REFUND]);

const summaryData = computed(() => {
  const rma = response.value.rmaList[0];
  if ([RETURN_RESOLUTIONS.REPLACEMENT, RETURN_RESOLUTIONS.EXCHANGE].includes(rma.returnResolution)) return { amountDue: rma.estimatedAmountDue };
  return {
    subtotal: rma.merchandiseTotal * -1,
    tax: rma.totalTaxes * -1,
    total: rma.estimatedRefund * -1,
    receiving: {
      subtotal: rma.substituteMerchandiseTotal,
      tax: rma.substituteTotalTaxes,
      total: rma.substituteEstimatedTotal,
      credit: rma.exchangeCredit * -1,
    },
    amountDue: rma.estimatedAmountDue,
  };
});
const ssreDropOffWindowInstruction = computed(() =>
  returnConfirmation.ssreReturnDropOffWindow.instruction(singleRMA.value.returnResolution?.toLowerCase()),
);

const showRefundInstructions =
  singleRMA.value &&
  singleRMA.value.returnResolution === RETURN_RESOLUTIONS.REFUND &&
  singleRMA.value.rmaType === RMA_DETAIL.RETURN_REQUIRED.TYPE &&
  !(singleRMA.value.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn && !singleRMA.value.isMkpOrder);

const RefundInstructions =
  showRefundInstructions &&
  markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "refund-instructions-slideout" */ '@/components/returns/RefundInfo')));

const notifications = computed(() => {
  const arr = [];
  const web = response.value.orderReturnAlertMessage.web || {};
  if (web.message) {
    arr.push({
      type: web.message_type || 'info',
      text: web.message,
    });
  }

  if (isError.value) {
    arr.unshift({
      type: 'error',
      text: RMA_SHIPPING_DETAILS_ERROR,
    });
  }

  // more notifications

  return arr;
});

const enhancedprintLabelData = computed(() => {
  const shippingLabels = shippingDetails.value.shippingLabels ?? [];
  const shippingLabel = shippingLabels[0] ?? {};

  const items = singleRMA.value.rmaLines.map((rmaLine) => ({
    upc: rmaLine.upc,
    returnQuantity: rmaLine.returnQuantity,
    currentPrice: rmaLine.unitPrice,
    itemTotal: rmaLine.itemTotal,
    itemDetails: {
      productId: rmaLine.productId,
      primaryImageFilePath: rmaLine.primaryImageFilePath,
      urlTemplate: rmaLine.urlTemplate,
      productName: rmaLine.itemDescription,
      upcNumber: rmaLine.upc,
      upcColor: rmaLine.color,
      upcSize: rmaLine.size,
      quantity: rmaLine.returnQuantity,
      hazmatDescription: rmaLine.hazmatDescription,
      vendorName: rmaLine.vendorName,
      mkpLineItemDetails: {
        sellerName: rmaLine.sellerName,
        partnerFulfillmentId: rmaLine.partnerFulfillmentId,
      },
    },
    isHazmatItem: rmaLine.isHazmatItem,
  }));

  const substituteItems = singleRMA.value.substituteLines?.map((substitueLine) => ({
    upc: substitueLine.upc,
    currentPrice: substitueLine.unitPrice,
    itemTotal: substitueLine.itemTotal,
    isHazmatItem: substitueLine.isHazmatItem,
    itemDetails: {
      productId: substitueLine.productId,
      primaryImageFilePath: substitueLine.primaryImageFilePath,
      urlTemplate: substitueLine.urlTemplate,
      productName: substitueLine.itemDescription,
      upcNumber: substitueLine.upc,
      upcColor: substitueLine.color,
      upcSize: substitueLine.size,
      vendorName: substitueLine.vendorName,
      quantity: substitueLine.returnQuantity,
      mkpLineItemDetails: {
        sellerName: substitueLine.sellerName,
      },
    },
  }));

  return {
    ...singleRMA.value,
    emailAddress: singleRMA.value.returnEmail,
    pendingReturnId: singleRMA.value.rmaId,
    labelImageUrl: singleRMA.value.isDcrRedesignEnabled ? singleRMA.value.labelImageUrl : shippingLabel?.base64Label,
    returnTrackingNumber: singleRMA.value.isDcrRedesignEnabled ? singleRMA.value.returnTrackingNumber : shippingLabel?.trackingNumber,
    items,
    substituteShipments: [{ substituteItems }],
  };
});

const printLabel = async () => {
  showPrintPreview.value = false;
  await nextTick();
  showPrintPreview.value = true;
  await nextTick();

  if (singleRMA.value.pidToken) {
    await getShippingDetails({ pidToken: singleRMA.value.pidToken });
    printLabelRef.value.$el.focus();
    await nextTick();
    if (!isError.value) window.print();
  } else if (singleRMA.value.rmaId && !singleRMA.value.isDcrResponse) {
    await getShippingDetailsByRmaId({ rmaId: singleRMA.value.rmaId });
    printLabelRef.value.$el.focus();
    await nextTick();
    if (!isError.value) window.print();
  } else window.print();
};

const medalliaSurveyInit = () => {
  const MEDALLIA_SURVEY = window.KAMPYLE_ONSITE_SDK;
  if (!MEDALLIA_SURVEY || typeof MEDALLIA_SURVEY.updatePageView !== 'function') return;
  global.ReturnResolutionOption = response.value.rmaList[0].returnResolution;
  global.pathwayForMedallia = response.value.rmaList[0].returnDropOffMethod;
  global.marketPlaceItem = isMarketplaceOrder.value?.toString();
  global.beneficiaryUserState = response.value.beneficiaryUserState;
  MEDALLIA_SURVEY.updatePageView();
};

const getScanText = computed(() => SCAN_QR_TEXT.config()[singleRMA.value.returnDropOffMethod]);

const showPrintLabel = computed(() => singleRMA.value.returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups);

const resendEmailRmaId = computed(() => {
  const rmaList = response.value?.rmaList || [];
  return rmaList[0]?.rmaId || false;
});

function resendEmailHandler() {
  registerModal({
    id: 'return-label',
    title: 'Where should we send your return confirmation?',
    width: '628px',
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/GetReturnLabel'))),
    props: {
      isReturnConfirmation: true,
      rmaId: resendEmailRmaId.value,
    },
  });
  toggleModal('return-label');
}

onMounted(() => {
  setTimeout(() => {
    medalliaSurveyInit();
  }, 300);
});
</script>

<style lang="scss">
.cta {
  @include breakpoint(medium) {
    max-width: rem-calc(342);
  }
  .summary-container {
    display: flex;
    align-items: flex-end;
  }
}
.print-return-submitted-label-container {
  display: none;
}
@media print {
  #nav-header-wrapper,
  #nav-header,
  #nav-footer {
    display: none;
  }

  section .banner-content {
    display: none !important;
  }

  .print-return-submitted-label-container {
    display: block;
    position: relative;
    overflow-y: visible;
  }
}
</style>
